var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Book } from "./Book.js";
import { Resource } from "./Resource.js";
import { Bkper } from "./Bkper.js";
import * as CollectionService from "../service/collection-service.js";
/**
 * This class defines a Collection of [[Books]].
 *
 * @public
 */
export class Collection extends Resource {
    constructor(payload, config) {
        super(payload);
        this.config = config;
    }
    /** @internal */
    getConfig() {
        return this.config || Bkper.globalConfig;
    }
    /**
     * Gets the unique identifier of this Collection.
     *
     * @returns The id of this Collection
     */
    getId() {
        return this.payload.id;
    }
    /**
     * Gets the name of this Collection.
     *
     * @returns The name of this Collection
     */
    getName() {
        return this.payload.name;
    }
    /**
     * Sets the name of the Collection.
     *
     * @param name - The name to set
     *
     * @returns This Collection, for chaining
     */
    setName(name) {
        this.payload.name = name;
        return this;
    }
    /**
     * Gets the username of the owner of this Collection
     *
     * @returns The Collection's owner username
     */
    getOwnerUsername() {
        return this.payload.ownerUsername;
    }
    /**
     * Gets the user permission for this Collection
     *
     * @returns The permission for the current user
     */
    getPermission() {
        return this.payload.permission
            ? this.payload.permission
            : undefined;
    }
    /**
     * Gets all Books of this collection.
     *
     * @returns All Books of this collection
     */
    getBooks() {
        let books = [];
        if (this.payload.books == null) {
            return books;
        }
        for (const bookPayload of this.payload.books) {
            let book = new Book(bookPayload, this.config);
            books.push(book);
        }
        return books;
    }
    /**
     * Adds Books to this Collection.
     *
     * @param books - The Books to add to this Collection
     *
     * @returns The added Book objects
     */
    addBooks(books) {
        return __awaiter(this, void 0, void 0, function* () {
            const collectionId = this.getId();
            if (collectionId && books.length > 0) {
                const bookList = { items: books.map((b) => b.json()) };
                let addedBooks = yield CollectionService.addBooksToCollection(collectionId, bookList, this.getConfig());
                return addedBooks.map((book) => new Book(book, this.config));
            }
            return [];
        });
    }
    /**
     * Removes Books from this Collection.
     *
     * @param books - The Books to remove from this Collection
     *
     * @returns The removed Book objects
     */
    removeBooks(books) {
        return __awaiter(this, void 0, void 0, function* () {
            const collectionId = this.getId();
            if (collectionId && books.length > 0) {
                const bookList = { items: books.map((b) => b.json()) };
                let removedBooks = yield CollectionService.removeBooksFromCollection(collectionId, bookList, this.getConfig());
                return removedBooks.map((book) => new Book(book, this.config));
            }
            return [];
        });
    }
    /**
     * Gets the last update date of this Collection
     *
     * @returns The Collection's last update timestamp, in milliseconds
     */
    getUpdatedAt() {
        return this.payload.updatedAt;
    }
    /**
     * Performs create new Collection.
     *
     * @returns The created Collection object
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield CollectionService.createCollection(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Performs update Collection, applying pending changes.
     *
     * @returns The updated Collection object
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            this.payload = yield CollectionService.updateCollection(this.payload, this.getConfig());
            return this;
        });
    }
    /**
     * Performs delete Collection.
     *
     * @returns The list of Books the user has access to that were affected by the deletion of this Collection
     */
    remove() {
        return __awaiter(this, void 0, void 0, function* () {
            let books = yield CollectionService.deleteCollection(this.payload, this.getConfig());
            return books.map((book) => new Book(book, this.config));
        });
    }
}
//# sourceMappingURL=Collection.js.map